import { MicrosoftAssessmentThresholds } from "../../constants.ts";
import type {
    MicrosoftAssessment,
    OneDrive,
    SearchMailboxesAssessmentFilters,
    SearchOneDriveAssessmentFilters,
    SearchSharePointAssessmentFilters,
    SearchTeamsAssessmentFilters,
    SearchUsersAssessmentFilters,
    SharePoint,
    Teams,
    User
} from "../types.ts";
import { getNow } from "@sg-protect/components";
import { createFaker } from "./mocks.ts";

const BYTES_PER_GB = 1_000_000_000;

const faker = createFaker("microsoft");

const teams = Array.from({ length: faker.number.int({ min: 12, max: 100 }) }).map<Teams>(() => ({
    siteId: faker.string.uuid(),
    groupId: faker.string.uuid(),
    isArchived: faker.datatype.boolean(0.2),
    privacy: faker.helpers.arrayElement(["public", "private"]),
    displayName: `${faker.company.catchPhraseAdjective()} ${faker.commerce.department()}`,
    webUrl: faker.internet.url(),
    contentSizeInBytes: faker.number.int({ min: 0, max: 9999999999 }),
    membersCount: faker.number.int({ min: 0, max: 1000 }),
    lastActivityDate: faker.helpers.maybe(() => faker.date.past().toISOString()),
    owners: Array.from({ length: faker.number.int({ min: 0, max: 3 }) }).map(() => ({
        id: faker.string.uuid(),
        displayName: faker.person.fullName(),
        jobTitle: faker.helpers.maybe(() => faker.person.jobType())
    })),
    channels: {
        publicChannelCount: faker.number.int({ min: 0, max: 100 }),
        privateChannelCount: faker.number.int({ min: 0, max: 100 }),
        sharedChannelCount: faker.number.int({ min: 0, max: 100 })
    }
}));

const tenantId = "eb39acb7-fae3-4bc3-974c-b765aa1d6355";
export const mocks = {
    assessments: {
        completed: {
            assessmentId: faker.string.uuid(),
            accountEmail: "contoso@sharegate.com",
            endedAt: "2023-10-01T12:00:00Z",
            status: "completed",
            reportsAreConcealed: false,
            tenantId
        } as MicrosoftAssessment,
        inProgress: {
            assessmentId: faker.string.uuid(),
            accountEmail: "alice@workleap.com",
            endedAt: null,
            status: "inProgress",
            reportsAreConcealed: false,
            tenantId: faker.string.uuid()
        } satisfies MicrosoftAssessment,
        failed: {
            assessmentId: faker.string.uuid(),
            accountEmail: "john@workleap.com",
            endedAt: null,
            status: "failed",
            reportsAreConcealed: false,
            tenantId: faker.string.uuid()
        } satisfies MicrosoftAssessment
    },
    tenant: {
        id: tenantId,
        name: "Contoso",
        domain: "contoso.com"
    },
    sharegate: {
        account: "00000000-0000-0000-0000-000000000000"
    },
    teams: {
        all: teams,
        filter: (filter: SearchTeamsAssessmentFilters) =>
            ({
                all: mocks.teams.all,
                largeContentSize: mocks.teams.all.filter(
                    team =>
                        team.contentSizeInBytes > MicrosoftAssessmentThresholds.teams.largeContentSizeGb * BYTES_PER_GB
                ),
                manyMembers: mocks.teams.all.filter(
                    team => team.membersCount > MicrosoftAssessmentThresholds.teams.manyMembersCount.actual
                ),
                inactive: mocks.teams.all.filter(
                    team =>
                        team.lastActivityDate &&
                        new Date(team.lastActivityDate) < daysAgo(MicrosoftAssessmentThresholds.teams.inactiveDays)
                ),
                orphaned: mocks.teams.all.filter(team => team.owners.length === 0)
            })[filter]
    },
    sharepoints: {
        all: [
            ...teams.map(
                x =>
                    ({
                        ...x,
                        type: "teams",
                        template: "Team site",
                        isTeamsConnected: true,
                        fileCount: faker.helpers.maybe(() => faker.number.int({ min: 0, max: 20000 }))
                    }) as SharePoint
            ),
            ...Array.from({ length: faker.number.int({ min: 12, max: 100 }) }).map(
                () =>
                    ({
                        siteId: faker.string.uuid(),
                        type: faker.helpers.arrayElement([
                            "unknown",
                            "group",
                            "oneDrive",
                            "sharePointSite",
                            "teamsChannel"
                        ]),
                        template: faker.helpers.arrayElement([
                            null,
                            "Team site",
                            "Team site (no Microsoft 365 group)",
                            "Project Site"
                        ]),
                        displayName: `${faker.company.catchPhraseAdjective()} ${faker.commerce.department()}`,
                        webUrl: faker.internet.url(),
                        contentSizeInBytes: faker.number.int({ min: 0, max: 9999999999 }),
                        externalSharingCount: faker.number.int({ min: 0, max: 100 }),
                        lastActivityDate: faker.helpers.maybe(() => faker.date.past().toISOString()),
                        fileCount: faker.helpers.maybe(() => faker.number.int({ min: 0, max: 20000 })),
                        isTeamsConnected: false,
                        isHubSite: faker.datatype.boolean(0.2),
                        owners:
                            faker.helpers.maybe(() =>
                                Array.from({ length: faker.number.int({ min: 0, max: 3 }) }).map(() => ({
                                    id: faker.string.uuid(),
                                    displayName: faker.person.fullName(),
                                    jobTitle: faker.helpers.maybe(() => faker.person.jobType())
                                }))
                            ) ?? null
                    }) as SharePoint
            )
        ],
        filter: (filter: SearchSharePointAssessmentFilters) =>
            ({
                all: mocks.sharepoints.all,
                largeContentSize: mocks.sharepoints.all.filter(
                    sharepoint =>
                        sharepoint.contentSizeInBytes >
                        MicrosoftAssessmentThresholds.sharePoint.largeContentSizeGb.actual * BYTES_PER_GB
                ),
                manyFiles: mocks.sharepoints.all.filter(
                    sharepoint =>
                        sharepoint.fileCount &&
                        sharepoint.fileCount > MicrosoftAssessmentThresholds.sharePoint.manyFilesCount.actual
                ),
                inactive: mocks.sharepoints.all.filter(
                    sharepoint =>
                        sharepoint.lastActivityDate &&
                        new Date(sharepoint.lastActivityDate) <
                            daysAgo(MicrosoftAssessmentThresholds.sharePoint.inactiveDays)
                )
            })[filter]
    },
    onedrives: {
        all: Array.from({ length: faker.number.int({ min: 12, max: 100 }) }).map(
            () =>
                ({
                    siteId: faker.string.uuid(),
                    displayName: faker.person.fullName(),
                    webUrl: faker.internet.url(),
                    lastModifiedDate: faker.helpers.maybe(() => faker.date.past().toISOString()),
                    lastActivityDate: faker.helpers.maybe(() => faker.date.past().toISOString()),
                    contentSizeInBytes: faker.number.int({ min: 0, max: 9999999999 }),
                    externalSharingCount: faker.number.int({ min: 0, max: 100 }),
                    fileCount: faker.helpers.maybe(() => faker.number.int({ min: 0, max: 20000 }))
                }) as OneDrive
        ),
        filter: (filter: SearchOneDriveAssessmentFilters) =>
            ({
                all: mocks.onedrives.all,
                largeContentSize: mocks.onedrives.all.filter(
                    onedrive =>
                        onedrive.contentSizeInBytes >
                        MicrosoftAssessmentThresholds.oneDrive.largeContentSizeGb.actual * BYTES_PER_GB
                ),
                manyFiles: mocks.onedrives.all.filter(
                    onedrive =>
                        onedrive.fileCount && onedrive.fileCount > MicrosoftAssessmentThresholds.oneDrive.manyFilesCount
                ),
                inactive: mocks.onedrives.all.filter(
                    onedrives =>
                        onedrives.lastActivityDate &&
                        new Date(onedrives.lastActivityDate) <
                            daysAgo(MicrosoftAssessmentThresholds.oneDrive.inactiveDays)
                )
            })[filter]
    },
    users: {
        all: Array.from({ length: faker.number.int({ min: 12, max: 100 }) }).map(
            () =>
                ({
                    userId: faker.string.uuid(),
                    displayName: faker.person.fullName(),
                    mail: faker.internet.email(),
                    userType: faker.helpers.arrayElement(["Member", "Guest"]),
                    licenses: Array.from({ length: faker.number.int({ min: 0, max: 3 }) }).map(() =>
                        faker.lorem.words({ min: 2, max: 5 })
                    )
                }) as User
        ),
        filter: (filter: SearchUsersAssessmentFilters) =>
            ({
                all: mocks.users.all,
                unlicensed: mocks.users.all.filter(user => user.licenses.length === 0),
                guests: mocks.users.all.filter(user => user.userType === "Guest")
            })[filter]
    },
    mailboxes: {
        all: Array.from({ length: faker.number.int({ min: 12, max: 100 }) }).map(() => ({
            userId: faker.string.uuid(),
            displayName: faker.person.fullName(),
            mail: faker.internet.email({ provider: "contoso.com" }),
            emailCount: faker.number.int({ min: 0, max: 50000 }),
            contentSizeInBytes: faker.number.int({ min: 3_000_000_000, max: 12_000_000_000 }),
            type: faker.helpers.arrayElement(["user", "shared", "room", "equipment", "unknown"]),

            folders: [
                {
                    wellKnownName: "inbox",
                    displayName: "Inbox",
                    contentSizeInBytes: faker.number.int({ min: 0, max: 3_000_000_000 }),
                    emailCount: faker.number.int({ min: 0, max: 50000 })
                },
                {
                    wellKnownName: "archive",
                    displayName: "Archive",
                    contentSizeInBytes: faker.number.int({ min: 0, max: 3_000_000_000 }),
                    emailCount: faker.number.int({ min: 0, max: 50000 })
                },
                {
                    wellKnownName: "junk",
                    displayName: "Junk",
                    contentSizeInBytes: faker.number.int({ min: 0, max: 3_000_000_000 }),
                    emailCount: faker.number.int({ min: 0, max: 50000 })
                },
                {
                    wellKnownName: "deleteditems",
                    displayName: "Deleted Items",
                    contentSizeInBytes: faker.number.int({ min: 0, max: 3_000_000_000 }),
                    emailCount: faker.number.int({ min: 0, max: 50000 })
                },
                ...Array.from({ length: faker.number.int({ min: 0, max: 5 }) }).map(() => ({
                    wellKnownName: null,
                    displayName: faker.lorem.slug(),
                    contentSizeInBytes: faker.number.int({ min: 0, max: 3_000_000_000 }),
                    emailCount: faker.number.int({ min: 0, max: 50000 })
                }))
            ]
        })),
        filter: (filter: SearchMailboxesAssessmentFilters) =>
            ({
                all: mocks.mailboxes.all,
                largeContentSize: mocks.mailboxes.all.filter(
                    mailbox =>
                        mailbox.contentSizeInBytes >
                        MicrosoftAssessmentThresholds.mailbox.largeContentSizeGb * BYTES_PER_GB
                )
            })[filter]
    }
};

const daysAgo = (days: number) => new Date(getNow() - days * 24 * 60 * 60 * 1000);
